import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Globe, Edit2, Heart, MessageCircle, Calendar, Save, X, Camera, Users, UserPlus, UserCheck, Send, Image as ImageIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function UserProfilePage() {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ bio: "", location: "", website: "" });
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [coverUrl, setCoverUrl] = useState(null);

  const isOwnProfile = currentUser?.user_id === userId;

  useEffect(() => {
    fetchProfile();
    fetchPosts();
    fetchFollowCounts();
    if (currentUser && !isOwnProfile) checkFollowing();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/users/profile/${userId}`);
      setProfile(res.data);
      setEditData({ bio: res.data.bio || "", location: res.data.location || "", website: res.data.website || "" });
      // Check cover photo
      setCoverUrl(`${API.replace('/api', '')}/api/users/cover/${userId}`);
    } catch { toast.error("Profile not found"); }
    finally { setLoading(false); }
  };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/users/profile/${userId}/posts?limit=20`);
      setPosts(res.data.posts || []);
    } catch {}
  };

  const fetchFollowCounts = async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}/follow-counts`);
      setFollowCounts(res.data);
    } catch {}
  };

  const checkFollowing = async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}/is-following`, { withCredentials: true });
      setIsFollowing(res.data.following);
    } catch {}
  };

  const toggleFollow = async () => {
    try {
      const res = await axios.post(`${API}/users/${userId}/follow`, {}, { withCredentials: true });
      setIsFollowing(res.data.following);
      fetchFollowCounts();
      toast.success(res.data.following ? "Following!" : "Unfollowed");
    } catch { toast.error("Please log in"); }
  };

  const fetchFollowers = async () => {
    try { const res = await axios.get(`${API}/users/${userId}/followers`); setFollowers(res.data || []); } catch {}
  };

  const fetchFollowing = async () => {
    try { const res = await axios.get(`${API}/users/${userId}/following`); setFollowing(res.data || []); } catch {}
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/users/profile`, editData, { withCredentials: true });
      toast.success("Profile updated!");
      setEditing(false);
      fetchProfile();
    } catch { toast.error("Failed"); }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.put(`${API}/users/profile/cover`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setCoverUrl(`${API.replace('/api', '')}/api/users/cover/${userId}?t=${Date.now()}`);
      toast.success("Cover updated!");
    } catch { toast.error("Upload failed"); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/users/profile/avatar`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Avatar updated!");
      fetchProfile();
    } catch { toast.error("Upload failed"); }
  };

  const getAvatarUrl = (p) => {
    if (!p?.picture) return null;
    return p.picture.startsWith('/api') ? `${API.replace('/api', '')}${p.picture}` : p.picture;
  };

  if (loading) return <Layout><div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-blue" /></div></Layout>;
  if (!profile) return <Layout><div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">User not found</p></div></Layout>;

  return (
    <Layout>
      {/* Cover Photo */}
      <div className="relative h-48 md:h-64 bg-gradient-to-r from-deep-blue to-[#1a3a5c] overflow-hidden">
        <img src={coverUrl} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
        {isOwnProfile && (
          <label className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:bg-black/70 flex items-center gap-1">
            <Camera className="w-3 h-3" /> Change Cover
            <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
          </label>
        )}
      </div>

      {/* Profile Header */}
      <section className="container-custom -mt-16 relative z-10 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
          {/* Avatar */}
          <div className="relative group">
            <div className="w-28 h-28 rounded-full bg-white border-4 border-white shadow-xl flex items-center justify-center text-deep-blue text-3xl font-bold overflow-hidden">
              {getAvatarUrl(profile) ? <img src={getAvatarUrl(profile)} alt="" className="w-full h-full object-cover" /> : profile.name?.charAt(0)?.toUpperCase()}
            </div>
            {isOwnProfile && (
              <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-full">
                <Camera className="w-6 h-6 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
              </label>
            )}
          </div>

          {/* Name + Stats */}
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-deep-blue">{profile.name}</h1>
              {(profile.role === "master_admin" || profile.role === "super_admin" || profile.role === "developer" || profile.role === "admin") && (
                <span className="bg-awareness-blue text-white text-xs px-2 py-0.5 rounded font-semibold">ADMIN</span>
              )}
            </div>
            {!editing && profile.bio && <p className="text-slate-600 mt-1 max-w-lg">{profile.bio}</p>}
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
              {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.location}</span>}
              {profile.website && <a href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-awareness-blue hover:underline"><Globe className="w-3 h-3" />{profile.website}</a>}
              {profile.created_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Joined {formatDistanceToNow(new Date(profile.created_at), { addSuffix: true })}</span>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {isOwnProfile ? (
              <Button variant="outline" size="sm" onClick={() => setEditing(!editing)}><Edit2 className="w-4 h-4 mr-1" /> Edit Profile</Button>
            ) : currentUser ? (
              <>
                <Button onClick={toggleFollow} variant={isFollowing ? "outline" : "default"} size="sm" className={isFollowing ? "" : "bg-deep-blue text-white"}>
                  {isFollowing ? <><UserCheck className="w-4 h-4 mr-1" /> Following</> : <><UserPlus className="w-4 h-4 mr-1" /> Follow</>}
                </Button>
                <Link to={`/dm/${userId}`}><Button variant="outline" size="sm"><Send className="w-4 h-4 mr-1" /> Message</Button></Link>
              </>
            ) : null}
          </div>
        </div>

        {/* Follow counts */}
        <div className="flex gap-6 mt-4 ml-0 md:ml-32">
          <button onClick={() => { setActiveTab("followers"); fetchFollowers(); }} className="text-center hover:text-awareness-blue">
            <span className="text-lg font-bold text-deep-blue">{followCounts.followers}</span>
            <span className="text-xs text-slate-500 ml-1">Followers</span>
          </button>
          <button onClick={() => { setActiveTab("following"); fetchFollowing(); }} className="text-center hover:text-awareness-blue">
            <span className="text-lg font-bold text-deep-blue">{followCounts.following}</span>
            <span className="text-xs text-slate-500 ml-1">Following</span>
          </button>
          <div className="text-center">
            <span className="text-lg font-bold text-deep-blue">{profile.stats?.posts || 0}</span>
            <span className="text-xs text-slate-500 ml-1">Posts</span>
          </div>
          <div className="text-center">
            <span className="text-lg font-bold text-deep-blue">{profile.stats?.likes_received || 0}</span>
            <span className="text-xs text-slate-500 ml-1">Likes</span>
          </div>
        </div>
      </section>

      {/* Edit Profile */}
      {isOwnProfile && editing && (
        <section className="container-custom mb-6">
          <div className="bg-white rounded-xl border p-4 max-w-lg space-y-3">
            <div><label className="text-sm font-medium">Bio</label><Textarea value={editData.bio} onChange={(e) => setEditData(p => ({...p, bio: e.target.value}))} maxLength={500} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Location</label><Input value={editData.location} onChange={(e) => setEditData(p => ({...p, location: e.target.value}))} /></div>
              <div><label className="text-sm font-medium">Website</label><Input value={editData.website} onChange={(e) => setEditData(p => ({...p, website: e.target.value}))} /></div>
            </div>
            <div className="flex gap-2"><Button onClick={handleSaveProfile} className="bg-deep-blue text-white"><Save className="w-4 h-4 mr-1" /> Save</Button><Button variant="outline" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button></div>
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="container-custom">
        <div className="flex gap-2 mb-6 border-b pb-2">
          {["posts", "followers", "following"].map(t => (
            <button key={t} onClick={() => { setActiveTab(t); if (t === "followers") fetchFollowers(); if (t === "following") fetchFollowing(); }}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium ${activeTab === t ? 'bg-deep-blue text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Posts Tab */}
        {activeTab === "posts" && (
          <div className="max-w-2xl space-y-4">
            {posts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border"><MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No posts yet</p></div>
            ) : posts.map(post => (
              <div key={post.post_id} className="bg-white rounded-xl border p-4">
                <p className="text-slate-700 text-sm whitespace-pre-wrap">{post.content}</p>
                {post.media?.length > 0 && post.media[0]?.url && (
                  <div className="mt-3 rounded-lg overflow-hidden">
                    {post.media[0].media_type === "video" ? (
                      <video src={`${API.replace('/api', '')}${post.media[0].url}`} controls className="max-h-60 rounded-lg" />
                    ) : (
                      <img src={`${API.replace('/api', '')}${post.media[0].url}`} alt="" className="max-h-60 rounded-lg object-contain" />
                    )}
                  </div>
                )}
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {post.likes_count || 0}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {post.comments_count || 0}</span>
                  <span>{post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : ""}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Followers/Following Tabs */}
        {(activeTab === "followers" || activeTab === "following") && (
          <div className="max-w-2xl space-y-2">
            {(activeTab === "followers" ? followers : following).length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border"><Users className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No {activeTab} yet</p></div>
            ) : (activeTab === "followers" ? followers : following).map(u => (
              <Link key={u.user_id} to={`/profile/${u.user_id}`} className="flex items-center gap-3 p-3 bg-white rounded-xl border hover:bg-slate-50">
                <div className="w-10 h-10 rounded-full bg-deep-blue flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                  {u.picture ? <img src={u.picture.startsWith('/api') ? `${API.replace('/api', '')}${u.picture}` : u.picture} alt="" className="w-full h-full object-cover" /> : u.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1"><span className="font-semibold text-deep-blue">{u.name}</span><p className="text-xs text-slate-400">{u.email}</p></div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
